import { memo, ReactNode } from 'react';

import { FieldInputProps, useField } from 'react-final-form';
import { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import isEqual from 'lodash/isEqual';

import { WysiwygInput as Input } from '@vk-hr-tek/ui/input';
import { BoxColors } from '@vk-hr-tek/ui/Box';

type WysiwygValue = {
  data: SerializedEditorState<SerializedLexicalNode> | undefined;
  hasContent: boolean;
};

interface WysiwygBody {
  data: SerializedEditorState<SerializedLexicalNode>;
  hasContent: boolean;
  isLimitExceeded: boolean;
}

type ValidateType = (
  value: WysiwygValue,
  allValues?: object,
) => (string | undefined) | Promise<string | undefined>;

const handleChange =
  (
    input: FieldInputProps<WysiwygValue, HTMLElement>,
    onChange?: (body: WysiwygBody) => void,
  ) =>
  (body: WysiwygBody) => {
    input.onChange(body.data);

    onChange?.(body);
  };

const handleBlur =
  (input: FieldInputProps<WysiwygValue, HTMLElement>) => () => {
    input.onBlur();
  };

const handleFocus =
  (input: FieldInputProps<WysiwygValue, HTMLElement>) => () => {
    input.onFocus();
  };

interface WysiwygInputProps {
  config?: {
    plugins: {
      selectAllPlugin: { value: boolean; withDivider: boolean };
      formatHeadingPlugin: {
        heading: boolean;
        fontColor: boolean;
        backgroungColor: boolean;
        withDivider: boolean;
      };
      formatTextPlugin: {
        bold: boolean;
        italic: boolean;
        underline: boolean;
        strikethrough: boolean;
        code: boolean;
        superscript: boolean;
        subscript: boolean;
        withDivider: boolean;
      };
      listAndFormatPlugin: {
        align: boolean;
        list: boolean;
        indent: boolean;
        withDivider: boolean;
      };
      linkAndTablePlugin: {
        link: boolean;
        table: boolean;
        reference: boolean;
        codeBlock: boolean;
        quote: boolean;
        symbols: boolean;
        mention: boolean;
        withDivider: boolean;
      };
      mediaPlugin: {
        singeImage: boolean;
        imagesGallery: boolean;
        video: boolean;
        file: boolean;
        withDivider: boolean;
      };
      undoRedoPlugin: boolean;
      trackMediaIdsAndMentionsPlugin: boolean;
    };
    mode: 'default' | 'comments';
  };
  focusMode?: 'auto' | 'manual';
  name: string;
  onChange?: (body: WysiwygBody) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  tooltip?: ReactNode | null;
  debounceTimeout?: number;
  maxCharLimit?: number;
  showCharLimitCounter?: boolean;
  loadFile: (
    file: File,
    type: 'file' | 'image',
  ) => Promise<{
    name: string;
    id: string;
    type: 'image' | 'file';
  }>;
  getAttachmentUrl: (id: string) => Promise<string>;
  searchMentions?: (params: {
    query: string;
    offset: number;
    limit: number;
  }) => Promise<{
    mentions: {
      id: string;
      type: 'user' | 'unit';
      data:
        | {
            id: string;
            first_name: string;
            second_name: string;
            middle_name: string;
            logo?: string;
          }
        | { id: string; name: string };
    }[];
    total: number;
  }>;
  onUploadError: (message: string) => void;
  validateArray?: ValidateType[];
  getCurrentMediaIds?: (mediaIds: string[]) => void;
  checkEditorContainsMention?: (hasMention: boolean) => void;
  getCurrentMentionsData?: (
    data: {
      id: string;
      type: string;
    }[],
  ) => void;
  handleSendClick?: () => Promise<'loading' | 'complete' | 'failed'>;
  onFocusEditor?: (focus: () => void) => void;
  editable?: boolean;
  hideSubmitButton?: boolean;
  bgcolor?: BoxColors;
  withoutPaddings?: boolean;
  apiUrl: string;
  envVars?: {
    allowDirectVideoLinks: boolean;
  };
  isSubmitting?: boolean;
  showErrors?: boolean;
  initialValue?: string | SerializedEditorState<SerializedLexicalNode>;
  flat?: boolean;
  stickyToolbar?: boolean;
}

const arePropsEqual = (
  oldProps: Record<string, unknown>,
  newProps: Record<string, unknown>,
) => isEqual(oldProps, newProps);

export const WysiwygInput = memo(
  ({
    config = {
      plugins: {
        selectAllPlugin: { value: true, withDivider: true },
        formatHeadingPlugin: {
          heading: true,
          fontColor: true,
          backgroungColor: true,
          withDivider: true,
        },
        formatTextPlugin: {
          bold: true,
          italic: true,
          underline: true,
          strikethrough: true,
          code: true,
          superscript: true,
          subscript: true,
          withDivider: true,
        },
        listAndFormatPlugin: {
          align: true,
          list: true,
          indent: true,
          withDivider: true,
        },
        linkAndTablePlugin: {
          link: true,
          table: true,
          reference: true,
          codeBlock: true,
          quote: true,
          symbols: true,
          mention: true,
          withDivider: true,
        },
        mediaPlugin: {
          singeImage: true,
          imagesGallery: true,
          video: true,
          file: true,
          withDivider: true,
        },
        undoRedoPlugin: true,
        trackMediaIdsAndMentionsPlugin: true,
      },
      mode: 'default',
    },
    name,
    onChange,
    placeholder,
    label,
    required = false,
    tooltip = null,
    loadFile,
    getAttachmentUrl,
    searchMentions,
    onUploadError,
    validateArray,
    getCurrentMediaIds,
    getCurrentMentionsData,
    checkEditorContainsMention,
    handleSendClick,
    onFocusEditor,
    focusMode = 'manual',
    debounceTimeout,
    maxCharLimit,
    showCharLimitCounter,
    editable,
    hideSubmitButton,
    bgcolor,
    withoutPaddings,
    apiUrl,
    envVars,
    isSubmitting = false,
    showErrors = true,
    initialValue,
    flat = false,
    stickyToolbar = true,
    ...rest
  }: WysiwygInputProps) => {
    const composeValidators =
      (validators: ValidateType[]) =>
      (value: WysiwygValue, allValues: object) => {
        const result = validators.reduce(
          (error: unknown, validator?: ValidateType) =>
            error || validator?.(value, allValues),
          undefined,
        );
        return result;
      };

    const { input, meta } = useField(name, {
      validate: composeValidators(validateArray || []),
      subscription: { value: true, touched: true, error: true },
      ...rest,
    });

    const helperText =
      meta.touched && typeof meta.error === 'string' ? meta.error : undefined;

    return (
      <Input
        {...input}
        initialValue={initialValue}
        apiUrl={apiUrl}
        envVars={envVars}
        config={config}
        onChange={handleChange(input, onChange)}
        onBlur={handleBlur(input)}
        onFocus={handleFocus(input)}
        placeholder={placeholder}
        label={label}
        required={required}
        tooltip={tooltip}
        error={meta.touched && !!meta.error}
        helperText={helperText}
        loadFile={loadFile}
        getAttachmentUrl={getAttachmentUrl}
        searchMentions={searchMentions}
        onUploadError={onUploadError}
        getCurrentMediaIds={getCurrentMediaIds}
        getCurrentMentionsData={getCurrentMentionsData}
        checkEditorContainsMention={checkEditorContainsMention}
        handleSendClick={handleSendClick}
        focusMode={focusMode}
        debounceTimeout={debounceTimeout}
        maxCharLimit={maxCharLimit}
        showCharLimitCounter={showCharLimitCounter}
        editable={editable}
        hideSubmitButton={hideSubmitButton}
        bgcolor={bgcolor}
        withoutPaddings={withoutPaddings}
        isSubmitting={isSubmitting}
        showErrors={showErrors}
        flat={flat}
        stickyToolbar={stickyToolbar}
      />
    );
  },
  arePropsEqual,
);
